import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { HexColorPicker } from 'react-colorful';
import {
  FiBold,
  FiItalic,
  FiCode,
  FiList,
  FiLink,
  FiImage,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiUnderline,
  FiCornerUpLeft,
  FiMaximize2,
  FiMinimize2,
  FiChevronDown,
  FiType,
  FiRotateCcw,
  FiRotateCw,
  FiSmile,
  FiHash,
  FiAlignLeft as FiImageLeft,
  FiAlignCenter as FiImageCenter,
  FiAlignRight as FiImageRight,
  FiMaximize,
  FiMinimize,
  FiCheck,
  FiX,
  FiUpload,
  FiTrash2,
  FiAlertCircle,
  FiRefreshCw,
  FiClock,
  FiUploadCloud,
  FiCheckCircle,
} from 'react-icons/fi';
import { GoListOrdered } from 'react-icons/go';
import { RxStrikethrough, RxFontSize } from 'react-icons/rx';

import { blogService } from '../../../services/blogService';

// ============================================
// READING TIME HELPER FUNCTIONS
// ============================================

const calculateReadingTime = content => {
  if (!content || typeof content !== 'string') return 1;
  const wordsPerMinute = 200;
  const plainText = content.replace(/<[^>]*>/g, '');
  const words = plainText
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);
  const wordCount = words.length;
  if (wordCount === 0) return 1;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(1, minutes);
};

const getFormattedReadingTime = minutes => {
  if (minutes === 1) return '1 min read';
  return `${minutes} min read`;
};

const getReadingTimeColor = minutes => {
  if (minutes <= 3) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  } else if (minutes <= 7) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  } else if (minutes <= 12) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  } else {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  }
};

// Custom Image Extension
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: {
        default: '100%',
        parseHTML: element =>
          element.style.width || element.getAttribute('width') || '100%',
        renderHTML: attributes => ({
          style: `width: ${attributes.width}; height: auto; display: block;`,
        }),
      },
      align: {
        default: 'center',
        renderHTML: attributes => {
          if (attributes.align === 'left') {
            return {
              style:
                'float: left; margin-right: 1rem; margin-bottom: 1rem; width: 50%; height: auto;',
            };
          }
          if (attributes.align === 'right') {
            return {
              style:
                'float: right; margin-left: 1rem; margin-bottom: 1rem; width: 50%; height: auto;',
            };
          }
          return {
            style:
              'display: block; margin-left: auto; margin-right: auto; width: 100%; height: auto;',
          };
        },
      },
      size: {
        default: 'full',
        renderHTML: attributes => {
          const sizeMap = {
            full: '100%',
            half: '50%',
            third: '33.33%',
            quarter: '25%',
          };
          const width = sizeMap[attributes.size] || '100%';
          return {
            style: `width: ${width}; height: auto; display: block; margin-left: auto; margin-right: auto;`,
          };
        },
      },
      isTemp: {
        default: false,
        parseHTML: element => element.getAttribute('data-temp') === 'true',
        renderHTML: attributes => ({
          'data-temp': attributes.isTemp ? 'true' : 'false',
        }),
      },
    };
  },
});

// ============================================
// INDIVIDUAL IMAGE UPLOAD & DELETE MODAL
// ============================================
const ImageActionModal = ({
  image,
  onConfirm,
  onCancel,
  onDelete,
  isUploading,
  uploadProgress,
  isDeleting,
}) => {
  const [error, setError] = useState(null);

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="text-center mb-4">
            <div className="w-20 h-20 mx-auto mb-4 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img
                src={image.previewUrl}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>
            <h3
              className="text-lg font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              {isUploading
                ? 'Uploading Image...'
                : isDeleting
                  ? 'Deleting Image...'
                  : 'Manage Image'}
            </h3>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {image.file ? image.file.name : 'Image'}
            </p>
            {image.file && (
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                {(image.file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
            {image.status === 'uploaded' && (
              <p className="text-xs mt-1 text-green-500 flex items-center justify-center gap-1">
                <FiCheckCircle className="w-3 h-3" /> Already uploaded
              </p>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {isUploading ? (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span style={{ color: 'var(--text)' }}>Uploading...</span>
                <span style={{ color: 'var(--muted)' }}>{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : isDeleting ? (
            <div className="mb-4 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                style={{ borderColor: 'var(--border)' }}
              >
                Cancel
              </button>
              {image.status !== 'uploaded' ? (
                <button
                  onClick={onConfirm}
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all flex items-center justify-center gap-2"
                >
                  <FiUploadCloud className="w-4 h-4" /> Upload
                </button>
              ) : (
                <button
                  onClick={onDelete}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" /> Delete from Cloudinary
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ============================================
// IMAGE MANAGEMENT MODAL - INDIVIDUAL UPLOAD
// ============================================
const ImageManagementModal = ({ images, onConfirm, onDelete, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleImageClick = image => {
    setSelectedImage(image);
    setShowActionModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!selectedImage) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
    }, 200);

    try {
      const formData = new FormData();
      formData.append('image', selectedImage.file);
      const response = await blogService.uploadImage(selectedImage.file);

      clearInterval(interval);
      setUploadProgress(100);

      setTimeout(() => {
        onConfirm(selectedImage, response.url, response.publicId);
        setShowActionModal(false);
        setSelectedImage(null);
        setIsUploading(false);
      }, 300);
    } catch (error) {
      clearInterval(interval);
      setIsUploading(false);
      console.error('Upload failed:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedImage) return;

    setIsDeleting(true);

    try {
      // If the image is already uploaded to Cloudinary, delete it
      if (selectedImage.status === 'uploaded' && selectedImage.publicId) {
        await blogService.deleteTempImage(selectedImage.publicId);
      }

      // Remove from the list - this will also trigger removal from editor
      onDelete(selectedImage);

      setTimeout(() => {
        setShowActionModal(false);
        setSelectedImage(null);
        setIsDeleting(false);
      }, 500);
    } catch (error) {
      setIsDeleting(false);
      console.error('Delete failed:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const handleActionCancel = () => {
    setShowActionModal(false);
    setSelectedImage(null);
    setIsUploading(false);
    setIsDeleting(false);
  };

  // Get the preview URL for display
  const getImagePreview = img => {
    if (img.uploadedUrl) return img.uploadedUrl;
    if (img.previewUrl) return img.previewUrl;
    return '';
  };

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={e => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-xl overflow-hidden flex flex-col"
            style={{ backgroundColor: 'var(--bg)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div>
                <h2
                  className="text-xl font-semibold"
                  style={{ color: 'var(--text)' }}
                >
                  Manage Images
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                  Click on an image to upload or delete it
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Stats Bar */}
            <div
              className="p-3 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-4 text-sm">
                <span style={{ color: 'var(--muted)' }}>
                  Total: {images.length} image(s)
                </span>
                <span className="text-yellow-500">
                  ⏳ Pending:{' '}
                  {images.filter(img => img.status === 'pending').length}
                </span>
                <span className="text-green-500">
                  ✓ Uploaded:{' '}
                  {images.filter(img => img.status === 'uploaded').length}
                </span>
              </div>
            </div>

            {/* Image Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <FiImage
                    className="w-16 h-16 mb-4"
                    style={{ color: 'var(--muted)' }}
                  />
                  <p className="text-lg" style={{ color: 'var(--text)' }}>
                    No images to manage
                  </p>
                  <p className="text-sm mt-2" style={{ color: 'var(--muted)' }}>
                    Insert images using the image button in the toolbar
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map(img => {
                    const isUploaded = img.status === 'uploaded';
                    const isPending = img.status === 'pending';
                    const previewUrl = getImagePreview(img);

                    return (
                      <div
                        key={img.tempId}
                        className={`group rounded-lg border-2 transition-all cursor-pointer overflow-hidden ${
                          isUploaded
                            ? 'border-green-500 hover:border-green-400'
                            : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                        }`}
                        onClick={() => handleImageClick(img)}
                      >
                        {/* Image Preview */}
                        <div className="aspect-square rounded-t-lg overflow-hidden bg-gray-100 dark:bg-gray-800 relative">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                          {/* Status Badge */}
                          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-sm">
                            {isUploaded ? (
                              <span className="text-green-500 flex items-center gap-1">
                                <FiCheckCircle className="w-3 h-3" /> Uploaded
                              </span>
                            ) : (
                              <span className="text-yellow-500 flex items-center gap-1">
                                <FiClock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </div>
                          {/* Click Hint */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                            <span className="text-white text-sm font-medium">
                              Click to manage
                            </span>
                          </div>
                        </div>

                        {/* File Info */}
                        <div
                          className="p-2 text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          <p className="truncate font-medium">
                            {img.file ? img.file.name : 'Image'}
                          </p>
                          {img.file && (
                            <p>{(img.file.size / 1024 / 1024).toFixed(2)} MB</p>
                          )}
                        </div>

                        {/* Status Indicator */}
                        <div
                          className="p-2 border-t flex items-center justify-between"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          <span className="text-xs">
                            {isUploaded ? '✅ Uploaded' : '⏳ Pending'}
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            Click to manage
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="p-4 border-t flex justify-end gap-2"
              style={{ borderColor: 'var(--border)' }}
            >
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                style={{ borderColor: 'var(--border)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Individual Action Modal */}
      {showActionModal && selectedImage && (
        <ImageActionModal
          image={selectedImage}
          onConfirm={handleConfirmUpload}
          onCancel={handleActionCancel}
          onDelete={handleDeleteImage}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

// Image Uploader with base64 preview
const ImageUploader = ({ editor, onImageInsert }) => {
  const handleImageInsert = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size should be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      editor
        .chain()
        .focus()
        .setImage({
          src: e.target.result,
          align: 'center',
          size: 'full',
          isTemp: true,
        })
        .run();

      if (onImageInsert) {
        onImageInsert(file, e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <input
        type="file"
        id="editor-image-upload"
        accept="image/*"
        multiple={false}
        onChange={handleImageInsert}
        className="hidden"
      />
      <ToolbarButton
        icon={FiImage}
        onClick={() => document.getElementById('editor-image-upload').click()}
        label="Insert Image"
      />
    </>
  );
};

// Image Formatting Toolbar
const ImageFormatToolbar = ({ editor }) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      const { state } = editor;
      const { selection } = state;
      const node = selection.$from.node(selection.$from.depth);

      if (node.type.name === 'image') {
        setSelectedImage(node);
        setShowToolbar(true);

        const view = editor.view;
        const coords = view.coordsAtPos(selection.from);
        setPosition({ top: coords.top - 50, left: coords.left });
      } else {
        setShowToolbar(false);
        setSelectedImage(null);
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor]);

  if (!showToolbar || !selectedImage || !editor) return null;

  const currentAlign = selectedImage.attrs.align || 'center';
  const currentSize = selectedImage.attrs.size || 'full';

  const setImageAlign = align => {
    editor.chain().focus().updateAttributes('image', { align }).run();
  };

  const setImageSize = size => {
    editor.chain().focus().updateAttributes('image', { size }).run();
  };

  return createPortal(
    <div
      className="fixed z-[9999] bg-white dark:bg-gray-900 rounded-lg shadow-lg border p-2 flex gap-1"
      style={{ top: position.top, left: position.left }}
    >
      <button
        onClick={() => setImageAlign('left')}
        className={`p-1.5 rounded ${currentAlign === 'left' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Align Left"
      >
        <FiImageLeft className="w-4 h-4" />
      </button>
      <button
        onClick={() => setImageAlign('center')}
        className={`p-1.5 rounded ${currentAlign === 'center' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Align Center"
      >
        <FiImageCenter className="w-4 h-4" />
      </button>
      <button
        onClick={() => setImageAlign('right')}
        className={`p-1.5 rounded ${currentAlign === 'right' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Align Right"
      >
        <FiImageRight className="w-4 h-4" />
      </button>
      <div className="w-px h-6 mx-1 bg-gray-300 dark:bg-gray-700" />
      <button
        onClick={() => setImageSize('full')}
        className={`p-1.5 rounded text-xs ${currentSize === 'full' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Full Width"
      >
        <FiMaximize className="w-3 h-3" />
      </button>
      <button
        onClick={() => setImageSize('half')}
        className={`p-1.5 rounded text-xs ${currentSize === 'half' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Half Width"
      >
        <FiMinimize className="w-3 h-3" />
      </button>
      <button
        onClick={() => setImageSize('third')}
        className={`p-1.5 rounded text-xs ${currentSize === 'third' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Third Width"
      >
        ⅓
      </button>
      <button
        onClick={() => setImageSize('quarter')}
        className={`p-1.5 rounded text-xs ${currentSize === 'quarter' ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
        title="Quarter Width"
      >
        ¼
      </button>
    </div>,
    document.body,
  );
};

// Toolbar Button Component
const ToolbarButton = ({ icon: Icon, isActive, onClick, disabled, label }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`p-2 rounded-lg transition-all duration-200 ${
      isActive
        ? 'bg-black/10 dark:bg-white/10 text-black dark:text-white'
        : 'text-gray-600 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    title={label}
  >
    <Icon className="w-4 h-4" />
  </button>
);

// Simple Dropdown with Portal
const SimpleDropdown = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleClickOutside = event => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  return (
    <>
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] rounded-xl shadow-2xl overflow-hidden border animate-fade-in-up"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
};

// Heading Dropdown
const HeadingDropdown = ({ editor }) => {
  const headings = [
    { level: 1, label: 'Heading 1' },
    { level: 2, label: 'Heading 2' },
    { level: 3, label: 'Heading 3' },
    { level: 4, label: 'Heading 4' },
    { level: 5, label: 'Heading 5' },
    { level: 6, label: 'Heading 6' },
  ];

  const getCurrentHeading = () => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return `H${i}`;
    }
    return 'Normal';
  };

  return (
    <SimpleDropdown
      trigger={
        <button className="px-3 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-2 text-sm">
          <FiType className="w-4 h-4" />
          <span>{getCurrentHeading()}</span>
          <FiChevronDown className="w-3 h-3" />
        </button>
      }
    >
      <div className="w-48">
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className="w-full px-4 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5"
          style={{ color: 'var(--text)' }}
        >
          Normal
        </button>
        {headings.map(heading => (
          <button
            key={heading.level}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleHeading({ level: heading.level })
                .run()
            }
            className="w-full px-4 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5"
            style={{ color: 'var(--text)' }}
          >
            {heading.label}
          </button>
        ))}
      </div>
    </SimpleDropdown>
  );
};

// Font Size Dropdown
const FontSizeDropdown = ({ editor }) => {
  const fontSizes = [
    { size: '12px', label: 'Small' },
    { size: '14px', label: 'Normal' },
    { size: '16px', label: 'Medium' },
    { size: '18px', label: 'Large' },
    { size: '20px', label: 'Extra Large' },
    { size: '24px', label: 'Huge' },
  ];

  return (
    <SimpleDropdown
      trigger={
        <button className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5">
          <RxFontSize className="w-4 h-4" />
        </button>
      }
    >
      <div className="w-36">
        {fontSizes.map(font => (
          <button
            key={font.size}
            onClick={() =>
              editor
                .chain()
                .focus()
                .setMark('textStyle', { fontSize: font.size })
                .run()
            }
            className="w-full px-4 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5"
            style={{ fontSize: font.size, color: 'var(--text)' }}
          >
            {font.label}
          </button>
        ))}
      </div>
    </SimpleDropdown>
  );
};

// Text Color Picker
const TextColorPicker = ({ editor }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [color, setColor] = useState('#000000');
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showPicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  }, [showPicker]);

  const presetColors = [
    '#000000',
    '#ef4444',
    '#3b82f6',
    '#22c55e',
    '#eab308',
    '#a855f7',
    '#ec4899',
    '#f97316',
  ];

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowPicker(!showPicker)}
        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1"
      >
        <div
          className="w-4 h-4 rounded-full border"
          style={{ backgroundColor: color, borderColor: 'var(--border)' }}
        />
        <FiChevronDown className="w-3 h-3" />
      </button>
      {showPicker &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-xl shadow-xl border p-3"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <HexColorPicker color={color} onChange={setColor} />
            <div className="grid grid-cols-4 gap-1 mt-3">
              {presetColors.map(preset => (
                <button
                  key={preset}
                  onClick={() => {
                    setColor(preset);
                    editor.chain().focus().setColor(preset).run();
                  }}
                  className="w-6 h-6 rounded-full border"
                  style={{
                    backgroundColor: preset,
                    borderColor: 'var(--border)',
                  }}
                />
              ))}
            </div>
            <button
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setShowPicker(false);
              }}
              className="w-full mt-3 px-3 py-1.5 text-sm rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
            >
              Reset Color
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

// Background Color Picker
const BackgroundColorPicker = ({ editor }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [color, setColor] = useState('#fef08a');
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showPicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  }, [showPicker]);

  const presetColors = [
    '#fef08a',
    '#fed7aa',
    '#fecaca',
    '#bbf7d0',
    '#bfdbfe',
    '#e9d5ff',
    '#fbcfe8',
  ];

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowPicker(!showPicker)}
        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1"
      >
        <div
          className="w-4 h-4 rounded-sm border"
          style={{ backgroundColor: color, borderColor: 'var(--border)' }}
        />
        <FiChevronDown className="w-3 h-3" />
      </button>
      {showPicker &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-xl shadow-xl border p-3"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <HexColorPicker color={color} onChange={setColor} />
            <div className="grid grid-cols-4 gap-1 mt-3">
              {presetColors.map(preset => (
                <button
                  key={preset}
                  onClick={() => {
                    setColor(preset);
                    editor
                      .chain()
                      .focus()
                      .setHighlight({ color: preset })
                      .run();
                  }}
                  className="w-6 h-6 rounded border"
                  style={{
                    backgroundColor: preset,
                    borderColor: 'var(--border)',
                  }}
                />
              ))}
            </div>
            <button
              onClick={() => {
                editor.chain().focus().unsetHighlight().run();
                setShowPicker(false);
              }}
              className="w-full mt-3 px-3 py-1.5 text-sm rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
            >
              Reset Background
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

// Emoji Picker
const EmojiPicker = ({ editor }) => {
  const [showPicker, setShowPicker] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const emojis = [
    '😀',
    '😃',
    '😄',
    '😁',
    '😆',
    '😅',
    '😂',
    '🤣',
    '😊',
    '😇',
    '🙂',
    '🙃',
    '😉',
    '😌',
    '😍',
    '🥰',
    '😘',
    '😗',
    '😋',
    '😛',
    '😝',
    '😜',
    '🤪',
    '🤨',
    '🧐',
    '🤓',
    '😎',
    '🤩',
    '🥳',
    '😏',
    '😒',
    '😞',
    '😔',
    '😟',
    '😕',
    '🙁',
    '😣',
    '😖',
    '😫',
    '😩',
    '🥺',
    '😢',
    '😭',
    '😤',
    '😠',
    '😡',
    '🤬',
    '🤯',
  ];

  useEffect(() => {
    if (showPicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  }, [showPicker]);

  const insertEmoji = emoji => {
    editor.chain().focus().insertContent(emoji).run();
    setShowPicker(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowPicker(!showPicker)}
        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
      >
        <FiSmile className="w-4 h-4" />
      </button>
      {showPicker &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-xl shadow-xl border p-3 w-80"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="grid grid-cols-8 gap-1 max-h-64 overflow-y-auto">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => insertEmoji(emoji)}
                  className="w-8 h-8 text-xl hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-all"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Special Characters Picker
const SpecialCharsPicker = ({ editor }) => {
  const [showPicker, setShowPicker] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const specialChars = [
    '©',
    '®',
    '™',
    '°',
    '±',
    '²',
    '³',
    '¹',
    '¼',
    '½',
    '¾',
    '€',
    '£',
    '¥',
    '¢',
    '§',
    '¶',
    '•',
    '·',
    '…',
    '—',
    '–',
    '✓',
    '✗',
    '★',
    '☆',
    '♥',
    '♦',
    '♣',
    '♠',
    '♪',
    '♫',
    '←',
    '→',
    '↑',
    '↓',
    '↔',
  ];

  useEffect(() => {
    if (showPicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  }, [showPicker]);

  const insertChar = char => {
    editor.chain().focus().insertContent(char).run();
    setShowPicker(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowPicker(!showPicker)}
        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
      >
        <FiHash className="w-4 h-4" />
      </button>
      {showPicker &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-xl shadow-xl border p-3 w-80"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="grid grid-cols-8 gap-1 max-h-64 overflow-y-auto">
              {specialChars.map(char => (
                <button
                  key={char}
                  onClick={() => insertChar(char)}
                  className="w-8 h-8 text-lg hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-all"
                >
                  {char}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Link Editor
const LinkEditor = ({ editor }) => {
  const [url, setUrl] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showEditor && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 5, left: rect.left });
    }
  }, [showEditor]);

  const handleAddLink = () => {
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
      setUrl('');
      setShowEditor(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowEditor(!showEditor)}
        className={`p-2 rounded-lg ${editor.isActive('link') ? 'bg-black/10 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
      >
        <FiLink className="w-4 h-4" />
      </button>
      {showEditor &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-xl shadow-xl border p-3 w-80"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <input
              type="text"
              placeholder="Enter URL (https://...)"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              onKeyPress={e => e.key === 'Enter' && handleAddLink()}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleAddLink}
                className="flex-1 px-3 py-1.5 rounded-lg bg-black text-white dark:bg-white dark:text-black text-sm"
              >
                Add
              </button>
              <button
                onClick={() => editor.chain().focus().unsetLink().run()}
                className="flex-1 px-3 py-1.5 rounded-lg border text-sm hover:bg-black/5 dark:hover:bg-white/5"
                style={{ borderColor: 'var(--border)' }}
              >
                Remove
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Main Editor Component
export const BlogEditor = ({
  content,
  onChange,
  onImagesChange,
  placeholder = 'Write your story...',
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [readingTime, setReadingTime] = useState(1);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pendingImages, setPendingImages] = useState([]);
  const [showImageManager, setShowImageManager] = useState(false);

  const handleImageInsert = useCallback((file, previewUrl) => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newImage = {
      file,
      previewUrl,
      tempId,
      insertedAt: Date.now(),
      status: 'pending',
    };
    setPendingImages(prev => [...prev, newImage]);
  }, []);

  const handleConfirmImageUpload = useCallback(
    async (imageToUpload, uploadedUrl, publicId) => {
      const existingImage = pendingImages.find(
        img => img.tempId === imageToUpload.tempId,
      );
      if (existingImage?.status === 'uploaded') {
        console.log(
          'Image already uploaded, skipping:',
          imageToUpload.file.name,
        );
        return uploadedUrl;
      }

      setPendingImages(prev =>
        prev.map(img =>
          img.tempId === imageToUpload.tempId
            ? { ...img, status: 'uploading' }
            : img,
        ),
      );

      if (editorRef.current) {
        const { state } = editorRef.current;
        const { doc } = state;

        let found = false;
        doc.descendants((node, pos) => {
          if (found) return false;
          if (
            node.type.name === 'image' &&
            node.attrs.src === imageToUpload.previewUrl
          ) {
            editorRef.current.commands.setNodeSelection(pos);
            editorRef.current.commands.updateAttributes('image', {
              src: uploadedUrl,
              isTemp: false,
            });
            found = true;
            return false;
          }
        });
      }

      setPendingImages(prev =>
        prev.map(img =>
          img.tempId === imageToUpload.tempId
            ? { ...img, status: 'uploaded', uploadedUrl, publicId }
            : img,
        ),
      );

      return uploadedUrl;
    },
    [pendingImages],
  );

  const editorRef = useRef(null);

  // FIXED: handleDeleteImage - removes image from editor content too
  const handleDeleteImage = useCallback(imageToDelete => {
    console.log('🗑️ [BlogEditor] Deleting image:', imageToDelete);

    // Remove from pendingImages
    setPendingImages(prev => {
      const newImages = prev.filter(img => img.tempId !== imageToDelete.tempId);
      console.log('📊 [BlogEditor] Images after removal:', newImages.length);
      return newImages;
    });

    // Remove from editor content
    if (editorRef.current) {
      const { state } = editorRef.current;
      const { doc } = state;

      // Find and remove the image from the editor
      let found = false;
      doc.descendants((node, pos) => {
        if (found) return false;
        if (
          node.type.name === 'image' &&
          (node.attrs.src === imageToDelete.previewUrl ||
            node.attrs.src === imageToDelete.uploadedUrl)
        ) {
          console.log(
            '🗑️ [BlogEditor] Found image in editor at position:',
            pos,
          );
          editorRef.current.commands.deleteRange({
            from: pos,
            to: pos + node.nodeSize,
          });
          found = true;
          return false;
        }
      });

      if (!found) {
        console.log('⚠️ [BlogEditor] Image not found in editor content');
      }
    }
  }, []);

  const openImageManager = useCallback(() => {
    if (pendingImages.length === 0) {
      alert('No images to manage. Insert images using the image button first.');
      return;
    }
    setShowImageManager(true);
  }, [pendingImages]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        link: false,
      }),
      CustomImage,
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Typography,
      Underline,
      TextStyle,
      Color,
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-lg max-w-none focus:outline-none min-h-[500px] px-4 py-6',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      const words = text
        .trim()
        .split(/\s+/)
        .filter(w => w.length > 0).length;
      setCharacterCount(text.length);
      setWordCount(words);
      setReadingTime(calculateReadingTime(html));
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  useEffect(() => {
    if (editor && content && isFirstLoad && content !== editor.getHTML()) {
      editor.commands.setContent(content);
      setReadingTime(calculateReadingTime(content));
      setIsFirstLoad(false);
    }
  }, [editor, content, isFirstLoad]);

  useEffect(() => {
    if (onImagesChange) {
      onImagesChange(pendingImages);
    }
  }, [pendingImages, onImagesChange]);

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse">Loading editor...</div>
      </div>
    );
  }

  const pendingCount = pendingImages.filter(
    img => img.status === 'pending',
  ).length;
  const uploadedCount = pendingImages.filter(
    img => img.status === 'uploaded',
  ).length;

  const toolbar = (
    <div
      className={`sticky top-0 z-10 border-b ${isFullscreen ? 'px-6' : ''}`}
      style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      <div className="flex flex-wrap items-center gap-1 p-2">
        <ToolbarButton
          icon={FiRotateCcw}
          onClick={() => editor.chain().focus().undo().run()}
          label="Undo"
        />
        <ToolbarButton
          icon={FiRotateCw}
          onClick={() => editor.chain().focus().redo().run()}
          label="Redo"
        />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <HeadingDropdown editor={editor} />
        <FontSizeDropdown editor={editor} />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <ToolbarButton
          icon={FiBold}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        />
        <ToolbarButton
          icon={FiItalic}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        />
        <ToolbarButton
          icon={FiUnderline}
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
        />
        <ToolbarButton
          icon={RxStrikethrough}
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label="Strikethrough"
        />
        <ToolbarButton
          icon={FiCode}
          isActive={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          label="Code"
        />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <ToolbarButton
          icon={FiList}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet List"
        />
        <ToolbarButton
          icon={GoListOrdered}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered List"
        />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <TextColorPicker editor={editor} />
        <BackgroundColorPicker editor={editor} />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <ToolbarButton
          icon={FiCode}
          isActive={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label="Code Block"
        />
        <ToolbarButton
          icon={FiCornerUpLeft}
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Blockquote"
        />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <ToolbarButton
          icon={FiAlignLeft}
          isActive={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          label="Left"
        />
        <ToolbarButton
          icon={FiAlignCenter}
          isActive={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          label="Center"
        />
        <ToolbarButton
          icon={FiAlignRight}
          isActive={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          label="Right"
        />
        <ToolbarButton
          icon={FiAlignJustify}
          isActive={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          label="Justify"
        />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <SpecialCharsPicker editor={editor} />
        <EmojiPicker editor={editor} />
        <div
          className="w-px h-6 mx-1"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <LinkEditor editor={editor} />
        <ImageUploader editor={editor} onImageInsert={handleImageInsert} />

        {pendingImages.length > 0 && (
          <ToolbarButton
            icon={FiUpload}
            onClick={openImageManager}
            label={`Manage Images (${pendingImages.length} total)`}
          />
        )}

        <div className="flex-1" />

        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getReadingTimeColor(readingTime)}`}
          title={`${wordCount} words • ~${readingTime} minute read`}
        >
          <FiClock className="w-3 h-3" />
          <span>{getFormattedReadingTime(readingTime)}</span>
        </div>

        <div
          className="flex items-center gap-2 text-xs px-2"
          style={{ color: 'var(--muted)' }}
        >
          <span>{characterCount} chars</span>
          <span>•</span>
          <span>{wordCount} words</span>
          {pendingCount > 0 && (
            <span className="text-yellow-500 font-medium">
              📷 {pendingCount} pending
            </span>
          )}
          {uploadedCount > 0 && (
            <span className="text-green-500">✓ {uploadedCount} uploaded</span>
          )}
        </div>

        <ToolbarButton
          icon={isFullscreen ? FiMinimize2 : FiMaximize2}
          onClick={() => setIsFullscreen(!isFullscreen)}
          label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        />
      </div>
    </div>
  );

  return (
    <div className="relative">
      {toolbar}
      <ImageFormatToolbar editor={editor} />
      <div
        className={`${isFullscreen ? 'fixed inset-0 z-50 overflow-auto pt-16' : ''}`}
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <EditorContent editor={editor} />
      </div>

      {showImageManager && (
        <ImageManagementModal
          images={pendingImages}
          onConfirm={handleConfirmImageUpload}
          onDelete={handleDeleteImage}
          onClose={() => setShowImageManager(false)}
        />
      )}
    </div>
  );
};
